import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ADVISOR_TYPES = [
  { value: 'cpa', label: 'CPA / Accounting Firm' },
  { value: 'attorney', label: 'Estate Attorney' },
  { value: 'financial_advisor', label: 'Financial Advisor / Wealth Manager' },
  { value: 'other', label: 'Other' },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    firmName: '',
    advisorType: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  function updateField(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        firmName: form.firmName,
        advisorType: form.advisorType || 'other',
      });
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-ivory-100 flex items-center justify-center px-8 py-12">
      <div className="w-full max-w-lg">
        <h1 className="font-display text-display-lg text-navy-900 mb-1">
          Get started
        </h1>
        <p className="text-sm text-navy-400 font-sans mb-8">
          Create your advisor account on Osprey Legacy
        </p>

        {error && (
          <div className="mb-6 p-3 bg-burgundy-50 border border-burgundy-200 rounded-md">
            <p className="text-sm text-burgundy-600 font-sans">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className="input"
                required
              />
            </div>
            <div>
              <label className="input-label">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className="input"
                required
              />
            </div>
          </div>

          <div>
            <label className="input-label">Firm Name</label>
            <input
              type="text"
              value={form.firmName}
              onChange={(e) => updateField('firmName', e.target.value)}
              className="input"
              placeholder="e.g. Smith & Associates CPA"
              required
            />
          </div>

          <div>
            <label className="input-label">Advisor Type</label>
            <select
              value={form.advisorType}
              onChange={(e) => updateField('advisorType', e.target.value)}
              className="input"
              required
            >
              <option value="">Select type...</option>
              {ADVISOR_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="input-label">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className="input"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                className="input"
                placeholder="Min 8 characters"
                required
              />
            </div>
            <div>
              <label className="input-label">Confirm Password</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => updateField('confirmPassword', e.target.value)}
                className="input"
                required
              />
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? 'Creating account...' : 'Create Advisor Account'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-navy-400 font-sans">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-navy-700 font-medium hover:text-navy-900 underline underline-offset-2"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
